import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLocation, matchPath, Link } from 'react-router-dom';

// material-ui
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// ant design icons
import DownOutlined from '@ant-design/icons/DownOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';

// project imports
import NavItem from './NavItem';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { usePermission } from 'hooks/usePermission';

// ==============================|| NAVIGATION - COLLAPSE ITEM ||============================== //

export default function NavCollapse({ item, level }) {
  // All hooks must be called before any conditional return (Rules of Hooks)
  const hasPermission = usePermission(item.permission ?? '');
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  const isChildSelected = item.children?.some((child) =>
    matchPath({ path: child.url, end: false }, pathname)
  );

  const [open, setOpen] = useState(!!isChildSelected);

  if (item.permission && !hasPermission) return null;

  const Icon = item.icon;
  const itemIcon = Icon ? (
    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
  ) : null;

  // Fixed light-on-dark palette for this rail's Twilight gradient background —
  // NavCollapse is only ever rendered inside the Drawer.
  const textColor = '#b9b4e6';
  const iconSelectedColor = '#ffffff';

  return (
    <>
      <ListItemButton
        disableRipple={!drawerOpen}
        onClick={() => {
          if (!drawerOpen) {
            handlerDrawerOpen(true);
            setOpen(true);
          } else {
            setOpen((prev) => !prev);
          }
        }}
        selected={isChildSelected}
        sx={{
          zIndex: 1201,
          ...(!drawerOpen && level === 1
            ? { flexDirection: 'column', justifyContent: 'center', px: 0.5 }
            : { pl: drawerOpen ? `${level * 28}px` : 1.5 }),
          py: !drawerOpen && level === 1 ? 1 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            '&.Mui-selected': {
              bgcolor: 'rgba(255,255,255,0.14)',
              borderRight: '2px solid',
              borderColor: '#ffffff',
              color: iconSelectedColor,
              '&:hover': { color: iconSelectedColor, bgcolor: 'rgba(255,255,255,0.14)' }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: !drawerOpen && level === 1 ? 0 : 28,
              color: isChildSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' }
              }),
              ...(!drawerOpen &&
                isChildSelected && {
                  bgcolor: '#ffffff',
                  color: '#302a80',
                  '&:hover': { bgcolor: '#f4f3fc', color: '#302a80' }
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {drawerOpen && (
          <>
            <ListItemText
              primary={
                <Typography variant="h6" sx={{ color: isChildSelected ? iconSelectedColor : textColor }}>
                  {item.title}
                </Typography>
              }
            />
            {open ? (
              <DownOutlined style={{ fontSize: '0.625rem', color: isChildSelected ? iconSelectedColor : textColor }} />
            ) : (
              <RightOutlined style={{ fontSize: '0.625rem', color: isChildSelected ? iconSelectedColor : textColor }} />
            )}
          </>
        )}
        {!drawerOpen && level === 1 && (
          <ListItemText
            sx={{ my: 0, mt: 0.5, width: '100%', minWidth: 0, textAlign: 'center' }}
            primary={
              <Typography
                sx={{
                  color: isChildSelected ? iconSelectedColor : textColor,
                  fontSize: '9px',
                  fontWeight: isChildSelected ? 700 : 500,
                  lineHeight: 1.25,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: 'block'
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
      </ListItemButton>

      <Collapse in={drawerOpen && open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ position: 'relative' }}>
          {item.children?.map((child) => (
            <NavItem key={child.id} item={child} level={level + 1} />
          ))}
        </List>
      </Collapse>
    </>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};